import { ref } from 'vue';

/**
 * Composable for handling image loading
 * Provides a function to wait for all images in the document to be loaded
 */
export function useImageLoading() {
  const isImagesLoaded = ref(false);

  /**
   * Wait for all images in the document to be loaded
   * @returns Promise that resolves when all images are loaded
   */
  const waitForImages = () => {
    return new Promise<void>((resolve) => {
      // Get all images in the document
      const images = document.querySelectorAll('img');
      
      // If there are no images, resolve immediately
      if (images.length === 0) {
        isImagesLoaded.value = true;
        resolve();
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;

      // Function to check if all images are loaded
      const checkAllImagesLoaded = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          isImagesLoaded.value = true;
          resolve();
        }
      };

      // Check each image
      images.forEach((img) => {
        // If the image is already loaded or has an error, increment counter
        if (img.complete) {
          checkAllImagesLoaded();
        } else {
          // Add event listeners for load and error events
          img.addEventListener('load', checkAllImagesLoaded);
          img.addEventListener('error', checkAllImagesLoaded);
        }
      });
    });
  };

  return {
    isImagesLoaded,
    waitForImages
  };
}